/**
 * Tenant Invitations API — platform-scoped administrator assignment.
 *
 * These endpoints operate on a NAMED tenant selected by path parameter and
 * require the platform grant `tenant.update`. They are the path for assigning an
 * administrator BEFORE that person has an account: the invitation confers a role
 * on an email, and becomes a membership when the invitee accepts.
 *
 * CONTRACT SOURCE: contracts/openapi/foundation.v1.yaml (register v1.4).
 * Every response is parsed by its Zod schema at the transport boundary.
 */
import { http, generateIdempotencyKey } from '@/api/client/http';
import {
  InvitationCreatedResponseSchema,
  InvitationListResponseSchema,
  InvitationSuccessResponseSchema,
} from '@/api/schemas/tenant-invitations';
import type {
  CreateInvitationRequest,
  Invitation,
  InvitationCreated,
} from '@/api/schemas/tenant-invitations';

export const TenantInvitationApi = {
  /**
   * POST /tenants/{tenantId}/invitations — invite an email to a role.
   *
   * The response carries the raw `token` EXACTLY ONCE (null on a replay). The
   * caller must surface it immediately and never persist it.
   */
  async create(tenantId: string, input: CreateInvitationRequest): Promise<InvitationCreated> {
    const response = await http.post(
      `/tenants/${tenantId}/invitations`,
      InvitationCreatedResponseSchema,
      { body: input, idempotencyKey: generateIdempotencyKey() },
    );
    return response.data;
  },

  /** GET /tenants/{tenantId}/invitations — the tenant's invitations (never the token). */
  async list(tenantId: string): Promise<Invitation[]> {
    const response = await http.get(
      `/tenants/${tenantId}/invitations`,
      InvitationListResponseSchema,
    );
    return response.data;
  },

  /** DELETE /tenants/{tenantId}/invitations/{invitationId} — revoke a pending invitation. */
  async revoke(tenantId: string, invitationId: string): Promise<Invitation> {
    const response = await http.delete(
      `/tenants/${tenantId}/invitations/${invitationId}`,
      InvitationSuccessResponseSchema,
      { idempotencyKey: generateIdempotencyKey() },
    );
    return response.data;
  },
};

export type { CreateInvitationRequest, Invitation, InvitationCreated };
